import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, CircleDashed, Copy, ExternalLink, Loader2, Minus, RefreshCw, X } from 'lucide-react';
import type { AskAction, AskApproval, AskEvent } from '../../api/assistantApi';
import type { TimelineItem } from '../../lib/askTimeline';

// Step status is always a word AND an icon, never color alone.
const STEP: Record<string, { label: string; icon: typeof Check; cls: string }> = {
  running: { label: 'Working', icon: Loader2, cls: 'text-blue-600 dark:text-blue-400 motion-safe:animate-spin' },
  succeeded: { label: 'Done', icon: Check, cls: 'text-blue-600 dark:text-blue-400' },
  reused: { label: 'Reused', icon: RefreshCw, cls: 'text-black/60 dark:text-white/60' },
  skipped: { label: 'Skipped', icon: Minus, cls: 'text-black/45 dark:text-white/45' },
  blocked: { label: 'Needs input', icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400' },
  failed: { label: 'Failed', icon: X, cls: 'text-red-600 dark:text-red-400' },
  pending: { label: 'Waiting', icon: CircleDashed, cls: 'text-black/40 dark:text-white/40' },
};

export function StepRow({ step }: { step: AskEvent }) {
  const meta = STEP[step.status ?? 'pending'] ?? STEP.pending;
  const Icon = meta.icon;
  const [openEv, setOpenEv] = useState(false);
  return (
    <li className="flex gap-2.5 py-1.5">
      <Icon size={15} className={`mt-0.5 shrink-0 ${meta.cls}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[13px] font-medium">{step.stage}</span>
          <span className="text-[11px] text-black/50 dark:text-white/50">{meta.label}</span>
          <time className="text-[11px] font-mono text-black/35 dark:text-white/35 ml-auto" dateTime={step.timestamp}>
            {new Date(step.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </time>
        </div>
        {step.summary && <p className="m-0 text-[13px] text-black/65 dark:text-white/65 break-words">{step.summary}</p>}
        {step.resource?.url && step.status !== 'running' && step.resource.url.startsWith('/') && (
          <Link className="text-[12px] text-blue-600 dark:text-blue-400 hover:underline" to={step.resource.url}>
            {step.resource.type === 'report' ? 'View report' : `View ${step.resource.type}`}
          </Link>
        )}
        {(step.evidence?.length ?? 0) > 0 && (
          <div className="mt-0.5">
            <button type="button" className="text-[12px] text-black/50 dark:text-white/50 hover:underline" aria-expanded={openEv} onClick={() => setOpenEv(!openEv)}>
              {openEv ? 'Hide' : 'Show'} evidence ({step.evidence!.length})
            </button>
            {openEv && (
              <ul className="m-0 mt-1 pl-4 text-[12px] space-y-0.5">
                {step.evidence!.map((ev, i) => (
                  <li key={i}>
                    {ev.url ? (
                      <a className="text-blue-600 dark:text-blue-400 hover:underline break-all" href={ev.url} target="_blank" rel="noopener noreferrer">
                        {ev.label}
                      </a>
                    ) : (
                      ev.label
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export function ActionButtons({ actions, onGo, onReply, disabled }: { actions: AskAction[]; onGo: (url: string) => void; onReply?: (text: string) => void; disabled?: boolean }) {
  const [copied, setCopied] = useState('');
  if (actions.length === 0) return null;
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      window.prompt('Copy this link', url);
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {actions.map((a) =>
        a.kind === 'reply' ? (
          <button key={a.label} type="button" disabled={disabled || !onReply} onClick={() => onReply?.(a.text ?? a.label)} className="inline-flex items-center h-8 px-3 rounded-lg text-[13px] font-medium border border-blue-600/40 text-blue-700 dark:text-blue-300 hover:bg-blue-600/10 disabled:opacity-50">
            {a.label}
          </button>
        ) : a.kind === 'open' ? (
          <button key={a.label} type="button" onClick={() => onGo(a.url)} className="inline-flex items-center h-8 px-3 rounded-lg text-[13px] font-medium bg-blue-600 text-white hover:bg-blue-700">
            {a.label}
          </button>
        ) : a.kind === 'external' ? (
          <a key={a.label} href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium bg-blue-600 text-white hover:bg-blue-700">
            {a.label} <ExternalLink size={13} aria-hidden />
          </a>
        ) : (
          <button key={a.label} type="button" onClick={() => copy(a.url)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium border border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10">
            <Copy size={13} aria-hidden /> {copied === a.url ? 'Copied' : a.label}
          </button>
        ),
      )}
    </div>
  );
}

export function ApprovalCard({ approval, busy, onDecide }: { approval: AskApproval; busy: boolean; onDecide: (d: 'approve' | 'deny') => void }) {
  const pending = approval.status === 'pending';
  const manage = approval.kind === 'manage';
  const label: Record<string, string> = manage
    ? { pending: 'Waiting for you', approved: 'Approved - applying…', denied: 'Declined - nothing changed', applied: 'Applied', failed: 'Did not complete' }
    : { pending: 'Waiting for you', approved: 'Approved - publishing…', denied: 'Declined - nothing changed', applied: 'Published', failed: 'Did not complete' };
  return (
    <section className="rounded-xl border border-black/10 dark:border-white/12 p-3 text-[13px]" aria-label={manage ? 'Changes confirmation' : 'Publishing confirmation'}>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="m-0 text-[14px] font-medium">{approval.title}</h3>
        <span className="text-[11px] text-black/50 dark:text-white/50 shrink-0">{label[approval.status] ?? approval.status}</span>
      </div>
      <p className="m-0 mt-2 text-[12px] font-medium text-black/60 dark:text-white/60">{manage ? 'What changes' : 'What becomes public'}</p>
      <ul className="m-0 mt-1 pl-4 space-y-0.5">
        {approval.discloses.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
      <p className="m-0 mt-2 text-[12px] font-medium text-black/60 dark:text-white/60">{manage ? 'What does not' : 'What never does'}</p>
      <ul className="m-0 mt-1 pl-4 space-y-0.5 text-black/65 dark:text-white/65">
        {approval.never.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>
      {(approval.warnings?.length ?? 0) > 0 && (
        <ul className="m-0 mt-2 pl-4 space-y-0.5 text-amber-700 dark:text-amber-400">
          {approval.warnings!.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {approval.error && <p className="m-0 mt-2 text-red-600 dark:text-red-400">{approval.error}</p>}
      {pending && (
        <div className="flex gap-2 mt-3">
          <button type="button" disabled={busy} onClick={() => onDecide('approve')} className="h-8 px-3 rounded-lg text-[13px] font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {manage ? 'Apply changes' : 'Make public'}
          </button>
          <button type="button" disabled={busy} onClick={() => onDecide('deny')} className="h-8 px-3 rounded-lg text-[13px] font-medium border border-black/15 dark:border-white/20 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50">
            {manage ? "Don't apply" : 'Keep private'}
          </button>
        </div>
      )}
    </section>
  );
}

export function Timeline({
  items,
  busy,
  runningExec,
  onDecide,
  onCancel,
  onGo,
  onReply,
}: {
  items: TimelineItem[];
  busy: boolean;
  runningExec?: string;
  onDecide: (id: string, d: 'approve' | 'deny') => void;
  onCancel: (execId: string) => void;
  onGo: (url: string) => void;
  onReply?: (text: string) => void;
}) {
  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-[13px] text-black/55 dark:text-white/55">
        <p className="m-0 font-medium text-black/75 dark:text-white/75">What channel do you need?</p>
        <p className="m-0 mt-1">Paste a link to any public data: a CSV, JSON, XML or RSS file, an Excel or Word file, a Google Sheet, a page with a table, a GitHub profile. Or ask: “Compare Apple and Microsoft revenue from sec.gov”. Zmist builds the channel, its chart and a report, then keeps it fresh daily.</p>
      </div>
    );
  }
  return (
    <ol className="m-0 p-0 list-none space-y-3">
      {items.map((it) => (
        <li key={it.key}>
          {it.type === 'message' && (
            <div className={it.event.role === 'user' ? 'flex flex-col items-end' : ''}>
              <div
                className={`max-w-[92%] rounded-xl px-3 py-2 text-[13.5px] whitespace-pre-wrap break-words ${
                  it.event.role === 'user' ? 'bg-black/[0.06] dark:bg-white/10' : 'border border-black/10 dark:border-white/10'
                }`}
              >
                {it.event.text}
              </div>
              {it.event.role === 'user' && it.event.context?.channel && (
                <span className="mt-0.5 text-[11px] text-black/40 dark:text-white/40">
                  on {it.event.context.channelTitle || it.event.context.channel}
                  {it.event.context.source === 'pinned' ? ' (pinned)' : ''}
                </span>
              )}
              {it.event.role !== 'user' && it.event.actions && <ActionButtons actions={it.event.actions} onGo={onGo} onReply={onReply} disabled={busy} />}
            </div>
          )}
          {it.type === 'run' && (
            <div className="rounded-xl border border-black/10 dark:border-white/10 px-3 py-1.5">
              <ul className="m-0 p-0 list-none divide-y divide-black/5 dark:divide-white/5">
                {it.steps.map((s) => (
                  <StepRow key={s.stepId} step={s} />
                ))}
              </ul>
              {runningExec === it.executionId && (
                <button type="button" onClick={() => onCancel(it.executionId)} className="my-1.5 text-[12px] text-black/50 dark:text-white/50 hover:underline">
                  Cancel this run
                </button>
              )}
            </div>
          )}
          {it.type === 'approval' && <ApprovalCard approval={it.approval} busy={busy} onDecide={(d) => onDecide(it.approval.id, d)} />}
          {it.type === 'result' && (
            <div>
              <p className="m-0 text-[13.5px] font-medium break-words">{it.event.text}</p>
              {it.event.actions && <ActionButtons actions={it.event.actions} onGo={onGo} onReply={onReply} disabled={busy} />}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
